import TrimbleMaps from "@trimblemaps/trimblemaps-js";
import TrimbleMapsDraw from "@trimblemaps/trimblemaps-draw";
import type { DrawCreateEvent, DrawDeleteEvent, DrawSelectionChangeEvent, DrawUpdateEvent } from "@trimblemaps/trimblemaps-draw";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import type { Feature, Geometry, Polygon } from "@trimblemaps/trimblemaps-js/geojson";
import { fetchApi } from "services/api";
import { VirtualTerminalViewModel } from "TypeGen/Assets/VirtualTerminals/virtual-terminal-view-model";
import { Input } from "@progress/kendo-react-inputs";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Label } from "@progress/kendo-react-labels";
import { homeIcon, xIcon } from "@progress/kendo-svg-icons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Title } from "utils/title";

const mapId = "virtual-terminal-map";

const vehicleClasses = [
  { text: 'All', value: null },
  { text: 'Cargo Vans/Sprinters', value: 1 },
  { text: 'Straights', value: 2 },
  { text: 'Tractors', value: 4 }
];

const displayedStatLegend = [
  {
    label: "Available",
    description: "In-service trucks currently inside the terminal zone."
  },
  {
    label: "Incoming",
    description: "Trucks already on a load that are expected to become available in this terminal within 24 hours."
  },
  {
    label: "Avg LA",
    description: "Average last-available wait time, in hours, for the currently available trucks in the zone."
  },
  {
    label: "Out 24h",
    description: "Outbound loads that originated in the terminal during the last 24 business hours."
  },
  {
    label: "In 24h",
    description: "Inbound loads that terminated in the terminal during the last 24 business hours."
  },
  {
    label: "Out 7d",
    description: "Outbound loads that originated in the terminal during the last 7 days."
  },
  {
    label: "In 7d",
    description: "Inbound loads that terminated in the terminal during the last 7 days."
  }
];

const statusInputLegend = [
  {
    label: "Pending In 24h",
    description: "Committed inbound loads due in the next 24 hours that are not yet booked or assigned."
  },
  {
    label: "Pending Out 24h",
    description: "Committed outbound loads due in the next 24 hours that are not yet booked or assigned."
  },
  {
    label: "Pending Out 7d",
    description: "Committed outbound loads due in the next 7 days that are not yet booked or assigned."
  }
];

const statusLegend = [
  {
    label: "Vehicle Deficit",
    description: "This is driven by too few available trucks for recent outbound activity or near-term outbound commitments."
  },
  {
    label: "Vehicle Surplus",
    description: "This means available trucks are outpacing recent outbound activity or trucks have been sitting too long."
  },
  {
    label: "Balanced",
    description: "Supply and demand are roughly in line based on recent activity."
  }
];

const VirtualTerminals = () => {
  const map = useRef<TrimbleMaps.Map>(null);
  const drawControl = useRef<TrimbleMapsDraw>(null);
  const previousLength = useRef(0);

  const [loading, setLoading] = useState<boolean>(true);
  const [terminals, setTerminals] = useState<Feature<Polygon>[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [legendVisible, setLegendVisible] = useState(false);
  const [newTerminal, setNewTerminal] = useState<Feature<Polygon> | null>(null);
  const [newTerminalName, setNewTerminalName] = useState<string>("");
  const [vehicleClass, setVehicleClass] = useState<number>(null);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<(number | string)[]>([]);
  const [showBookedPoints, setShowBookedPoints] = useState<boolean>(true);

  const toggleBookedPointsVisibility = () => {
    setShowBookedPoints((prev) => !prev);

    if (map.current) {
      const visibility = showBookedPoints ? "none" : "visible";
      ["clusteredPoints", "clusterCount", "unclusteredPoints"].forEach((layerId) => {
        if (map.current.getLayer(layerId)) {
          map.current.setLayoutProperty(layerId, "visibility", visibility);
        }
      });
    }
  };

  const addTerminal = useCallback((terminal: Feature<Polygon>, name: string) => {
    fetchApi("/api/VirtualTerminal/Add", { name, polygon: terminal.geometry }, "POST")
      .then((response: { VirtualTerminalID: number }) => {
        setTerminals(prev => [...prev, { ...terminal, id: response.VirtualTerminalID, properties: { ...terminal.properties, Name: name } }]);
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Unable to add terminal. Please try again.');
      });
  }, []);

  const updateTerminal = useCallback((updatedTerminal: Feature<Polygon>) => {
    fetchApi(`/api/VirtualTerminal/Update/${updatedTerminal.id}`, { polygon: updatedTerminal.geometry }, "PUT")
      .then(() => {
        setTerminals(prev => {
          const index = prev.findIndex(t => t.id === updatedTerminal.id);
          if (index !== -1) {
            const updatedTerminals = [...prev];
            updatedTerminals[index] = {
              ...updatedTerminals[index],
              geometry: updatedTerminal.geometry
            };
            return updatedTerminals;
          }
          return prev;
        });
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Unable to update terminal. Please try again.');
      });
  }, []);

  const removeTerminal = useCallback((id: number) => {
    if (!window.confirm("Are you sure you want to remove this terminal?")) {
      // Need to refresh the map to add back the terminal if the user presses delete key on polygon
      return;
    }

    fetchApi(`/api/VirtualTerminal/Remove/${id}`, {}, "DELETE")
      .then(() => {
        setTerminals(prev => prev.filter(t => t.id !== id));
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Unable to remove terminal. Please try again.');
      });
  }, []);

  useEffect(() => {
    if (!mapLoaded) return;

    // Fetch and add recent stops layer
    fetchApi('/api/VirtualTerminal/RecentStops', { VehicleTypeID: vehicleClass }, 'POST')
    .then((data: Array<{ Label: string, Pickup: boolean, Latitude: number, Longitude: number }>) => {
      const features = data.map((d) => ({
        type: 'Feature' as const,
        properties: {
          label: d.Label,
          pickup: d.Pickup
        },
        geometry: {
          type: 'Point',
          coordinates: [d.Longitude, d.Latitude]
        } as Geometry
      }));

      map.current.getLayer('unclusteredPoints') && map.current.removeLayer('unclusteredPoints');
      map.current.getLayer('clusterCount') && map.current.removeLayer('clusterCount');
      map.current.getLayer('clusteredPoints') && map.current.removeLayer('clusteredPoints');
      map.current.getSource('bookedPoints') && map.current.removeSource('bookedPoints');

      map.current.addSource('bookedPoints', {
        type: 'geojson',
        cluster: true,
        clusterRadius: 40,
        clusterMaxZoom: 14,
        data: {
          type: 'FeatureCollection',
          features: features
        },
        clusterProperties: {
          pickup_count: ['+', ['get', 'pickup']],
        }
      });

      // Find the first draw layer to place clusters below it
      const firstDrawLayer = map.current.getStyle().layers.find(layer => layer.id.startsWith('gl-draw'))?.id;

      map.current.addLayer({
        id: 'clusteredPoints',
        type: 'circle',
        source: 'bookedPoints',
        filter: ['has', 'point_count'],
        paint: {
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            10, 10,
            13, 25,
            16, 50,
            19, 75,
            22, 100,
            25, 300,
            28, 1000,
            31, 2000,
            34, 5000,
            37, 10000,
            40
          ],
          'circle-color': [
            'interpolate-hcl',
            ['linear'],
            ['/', ['get', 'pickup_count'], ['get', 'point_count']],
            0, '#FF0000', // Red for only deliveries
            0.25, '#FF5A5F', // Red for more deliveries
            0.5, '#F1C40F', // Yellow for balance (equal pickups and deliveries)
            .75, '#2ECC71',  // Green for more pickups
            1, '#008000' // Green for only pickups
          ],
          'circle-stroke-color': '#FFF',
          'circle-stroke-width': 3,
          'circle-opacity': 0.5
        }
      }, firstDrawLayer);

      map.current.addLayer({
        id: 'clusterCount',
        type: 'symbol',
        source: 'bookedPoints',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count}',
          'text-font': ['Roboto Regular'],
          'text-size': 12
        },
        paint: {
          'text-color': '#FFF'
        }
      }, firstDrawLayer);

      map.current.addLayer({
        id: 'unclusteredPoints',
        type: 'circle',
        source: 'bookedPoints',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': 10,
          'circle-color': [
            'interpolate',
            ['linear'],
            ['get', 'pickup'],
            0, '#FF0000', // Red for deliveries
            1, '#008000'  // Green for pickups
          ],
          'circle-stroke-color': '#FFF',
          'circle-stroke-width': 2,
          'circle-opacity': 0.5
        }
      }, firstDrawLayer);

      setShowBookedPoints(true);
    });
  }, [mapLoaded, vehicleClass]);

  useEffect(() => {
    const url = vehicleClass ? `/api/VirtualTerminal/List?vehicleClass=${vehicleClass}` : "/api/VirtualTerminal/List";
    fetchApi(url)
      .then((response: { VirtualTerminals: VirtualTerminalViewModel[] }) => {
        setTerminals(response.VirtualTerminals.map((terminal) => ({
          type: "Feature",
          id: terminal.ID,
          properties: {
            Name: terminal.Name,
            Stats: terminal.Stats,
            Status: terminal.Stats?.CalculatedStatus
          },
          geometry: {
            type: terminal.Polygon.type as "Polygon",
            coordinates: terminal.Polygon.coordinates
          }
        })));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [vehicleClass]);

  useEffect(() => {
    if (map.current && mapLoaded) {
      map.current.resize();
    }
  }, [mapLoaded]);

  useEffect(() => {
    const style = localStorage["darkmode"] === 'false' ? TrimbleMaps.Common.Style.TRANSPORTATION : TrimbleMaps.Common.Style.TRANSPORTATION_DARK;
    map.current = new TrimbleMaps.Map({
      container: mapId,
      style: style,
      center: [-98.5795, 39.8283],
      zoom: 3,
      attributionControl: false,
    });

    map.current.on('load', () => {
      setMapLoaded(true);
    });

    map.current.addControl(new TrimbleMaps.NavigationControl());
    map.current.addControl(new TrimbleMaps.ScaleControl({ unit: 'imperial' }));
    map.current.addControl(new TrimbleMaps.FullscreenControl());
    map.current.setDarkMode(style === TrimbleMaps.Common.Style.TRANSPORTATION_DARK);

    drawControl.current = new TrimbleMapsDraw({
      controls: { polygon: true, rectangle: true, trash: true },
      displayControlsDefault: false,
      defaultMode: 'simple_select'
    });
    map.current.addControl(drawControl.current, "top-left");

    map.current.on("draw.selectionchange", (e: DrawSelectionChangeEvent) => {
      setSelectedFeatureIds(e.features.map(f => f.id));
    });

    map.current.on("draw.create", (e: DrawCreateEvent) => {
      if (e.features[0].geometry.type === 'Polygon') {
        const newTerminal = e.features[0] as Feature<Polygon>;
        setNewTerminal(newTerminal);
        setDialogVisible(true); // Show dialog to capture terminal name
      }
    });

    map.current.on("draw.update", (e: DrawUpdateEvent) => {
      if (e.features[0].geometry.type === 'Polygon') {
        updateTerminal({
          id: e.features[0].id,
          properties: {},
          type: "Feature",
          geometry: e.features[0].geometry as Polygon
        });
      }
    });

    map.current.on("draw.delete", (e: DrawDeleteEvent) => {
      const deletedIds = e.features.map(feature => feature.id);
      deletedIds.forEach(id => removeTerminal(id as number));
    });

    return () => {
      map.current.remove();
    };
  }, [updateTerminal, removeTerminal]);

  useEffect(() => {
    if (!map.current || !drawControl.current) return;

    // Clear existing terminals from the draw control
    drawControl.current.getAll().features.forEach((feature) => {
      drawControl.current.delete(feature.id.toString());
    });

    if (terminals.length > 0) {
      terminals.forEach((terminal) => {
        drawControl.current.add(terminal);
      });

      // Update bounds only if terminal length has changed
      if (terminals.length !== previousLength.current) {
        const bounds = new TrimbleMaps.LngLatBounds();
        terminals.forEach((terminal) => {
          terminal.geometry.coordinates[0].forEach((c) => {
            if (Array.isArray(c) && c.length === 2) {
              bounds.extend(c as [number, number]);
            }
          });
        });

        map.current.fitBounds(bounds, { padding: 50 });
      }

      // No terminals with status, skip adding the layer
      if (terminals.filter(t => t.properties.Status).length === 0) {
        return; 
      }

      // Remove existing terminal polygons layer if it exists
      if (map.current.getLayer('terminal-polygons')) {
        map.current.removeLayer('terminal-polygons');
        map.current.removeSource('terminal-polygons');
      }

      map.current.addLayer({
        id: 'terminal-polygons',
        type: 'fill',
        source: {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: terminals,
          },
        },
        paint: {
          'fill-color': [
            'case',
            ['==', ['get', 'Status'], 'Surplus'], '#FF0000',
            ['==', ['get', 'Status'], 'Deficit'], '#008000',
            '#ffc107',
          ],
          'fill-opacity': 0.5,
        },
      });
    }

    previousLength.current = terminals.length;
  }, [terminals]);

  const handleSaveTerminal = () => {
    if (newTerminal && newTerminalName.trim()) {
      addTerminal(newTerminal, newTerminalName);
      setNewTerminal(null);
      setNewTerminalName("");
      setDialogVisible(false);
    } else {
      alert("Please enter a terminal name.");
    }
  };

  const handleCancelTerminal = () => {
    // Remove the drawn terminal from the map
    if (drawControl.current) {
      const feature = drawControl.current.getAll();
      feature.features.forEach(f => {
        if (f.id === newTerminal?.id) {
          drawControl.current.delete(f.id.toString());
        }
      });
    }

    setNewTerminal(null);
    setNewTerminalName("");
    setDialogVisible(false);
  };

  const home = () => {
    window.location.href = "/";
  };

  return (
    <div className="w-100 vh-100 d-flex">
      <Title string="Virtual Terminals" />
      <section className="col-3 p-2 overflow-auto">
        <Button themeColor="primary" className="w-100 mb-2" svgIcon={homeIcon} onClick={home}>HOME</Button>
        <Button themeColor="secondary" className="w-100 mb-2" onClick={toggleBookedPointsVisibility}>
          {showBookedPoints ? "Hide Stop Clusters" : "Show Stop Clusters"}
        </Button>
        <Button themeColor="info" className="w-100 mb-2" onClick={() => setLegendVisible(true)}>
          Legend
        </Button>
        <DropDownList
          label="Vehicle Size"
          className="mb-2"
          style={{ width: "100%" }}
          data={vehicleClasses}
          value={vehicleClasses.find(v => v.value === vehicleClass)}
          onChange={(e) => setVehicleClass(e.value.value)}
          textField="text"
          dataItemKey="value"
        />
        <ul className="list-group">
          {terminals.map((terminal) => (
            <li
              key={terminal.id}
              className={`list-group-item d-flex justify-content-between align-items-center ${
                selectedFeatureIds.includes(terminal.id) ? "bg-info" : ""
              }`}
              onClick={() => {
                setSelectedFeatureIds([terminal.id]);

                // Select the terminal on the map
                if (drawControl.current) {
                  const feature = drawControl.current.get(terminal.id.toString());
                  if (feature) {
                    drawControl.current.changeMode('simple_select', { featureIds: [terminal.id.toString()] });
                  }
                }

                // Fit the map to the terminal bounds
                const bounds = new TrimbleMaps.LngLatBounds();
                terminal.geometry.coordinates[0].forEach((c) => {
                  if (Array.isArray(c) && c.length === 2) {
                    bounds.extend(c as [number, number]);
                  }
                });
                map.current.fitBounds(bounds, { padding: 50 });
              }}
            >
              <div className="flex-grow-1">
                <div className="fw-bold">{terminal.properties.Name}</div>
                {terminal.properties.Stats && vehicleClass && (
                  <div className="small">
                    <div>Available: {terminal.properties.Stats.AvailableTrucks} | Incoming: {terminal.properties.Stats.IncomingTrucks} | Avg LA: {Math.trunc(terminal.properties.Stats.AvailableTrucksAvgLastAvailableHours)}h</div>
                    <div>Out 24h: {terminal.properties.Stats.OutboundLoadsPast24Hours} | In 24h: {terminal.properties.Stats.InboundLoadsPast24Hours}</div>
                    <div>Out 7d: {terminal.properties.Stats.OutboundLoadsPastWeek} | In 7d: {terminal.properties.Stats.InboundLoadsPastWeek}</div>
                    <div className={`badge ${
                      terminal.properties.Stats.CalculatedStatus === 'Surplus' ? 'badge-danger' : 
                      terminal.properties.Stats.CalculatedStatus === 'Deficit' ? 'badge-success' : 
                      'badge-warning'
                    }`}>
                      {terminal.properties.Stats.CalculatedStatus}
                    </div>
                  </div>
                )}
              </div>
              <Button themeColor="error" onClick={() => removeTerminal(terminal.id as number)} svgIcon={xIcon} />
            </li>
          ))}
        </ul>
      </section>
      <section className="col-9 p-0">
        <div id={mapId} className="w-100 h-100" />
      </section>

      {dialogVisible && <Dialog title="Add Terminal" onClose={handleCancelTerminal} className="dialog-w525">
        <Label>Enter a name for the new terminal:</Label>
        <Input
          required
          placeholder="Terminal Name"
          value={newTerminalName}
          onChange={(e) => setNewTerminalName(e.value)}
        />
        <DialogActionsBar>
          <Button onClick={handleCancelTerminal}>Cancel</Button>
          <Button themeColor="primary" onClick={handleSaveTerminal}>Add Terminal</Button>
        </DialogActionsBar>
      </Dialog>}

      {legendVisible && <Dialog title="Virtual Terminal Legend" onClose={() => setLegendVisible(false)} className="dialog-w525">
        <div className="mb-3">
          <div className="fw-bold mb-2">Displayed Stats</div>
          {displayedStatLegend.map((item) => (
            <div key={item.label} className="small mb-2">
              <span className="fw-bold">{item.label}:</span> {item.description}
            </div>
          ))}
        </div>

        <div className="mb-3">
          <div className="fw-bold mb-2">Status Inputs</div>
          <div className="small mb-2">
            Pending load metrics are not listed on each terminal card, but they are used when the terminal status is calculated.
          </div>
          {statusInputLegend.map((item) => (
            <div key={item.label} className="small mb-2">
              <span className="fw-bold">{item.label}:</span> {item.description}
            </div>
          ))}
        </div>

        <div className="mb-2">
          <div className="fw-bold mb-2">Status Meaning</div>
          {statusLegend.map((item) => (
            <div key={item.label} className="small mb-2">
              <span className="fw-bold">{item.label}:</span> {item.description}
            </div>
          ))}
        </div>

        <DialogActionsBar>
          <Button themeColor="primary" onClick={() => setLegendVisible(false)}>Close</Button>
        </DialogActionsBar>
      </Dialog>}
    </div>
  );
};

export default VirtualTerminals;
